import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, Grid, List, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getProducts, getCategories } from '../services/firebaseService';

const PRODUCT_PLACEHOLDER = "https://pub-2603f7a1a1604332b276f2ff8fff86f5.r2.dev/polymax/placeholder.png";

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [productsData, categoriesData] = await Promise.all([
        getProducts(),
        getCategories(),
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1e3a5f] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pt-24" data-testid="products-page">
      {/* Header */}
      <div className="bg-[#1e3a5f] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-white/60 mb-4">
            <Link to="/" className="hover:text-white">Ana Səhifə</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-white">Məhsullar</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white">
            Məhsullarımız
          </h1>
      
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex flex-1 gap-4 w-full sm:w-auto">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Məhsul axtar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Kateqoriya" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Bütün Kateqoriyalar</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">
                {filteredProducts.length} məhsul
              </span>
              <div className="flex border rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="rounded-none"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">Məhsul tapılmadı</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Link key={product.id} to={`/products/${product.id}`} className="group">
                <Card className="card-hover overflow-hidden">
                  <div className="aspect-square bg-slate-100 relative overflow-hidden">
                    <img
                      src={product.image_url || PRODUCT_PLACEHOLDER}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                    />
                    {getCategoryName(product.category_id) && (
                      <span className="absolute top-3 left-3 bg-[#1e3a5f] text-white text-xs px-3 py-1 rounded-full">
                        {getCategoryName(product.category_id)}
                      </span>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-[#1e3a5f] mb-2 line-clamp-1 group-hover:text-[#3b82f6] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3 min-h-[40px] whitespace-pre-line">
                      {product.description || 'Keyfiyyətli dərman'}
                    </p>
                    <div className="flex items-center justify-between">
                      {product.show_price && product.price ? (
                        <p className="font-bold text-[#3b82f6] text-lg">{product.price} AZN</p>
                      ) : (
                        <span></span>
                      )}
                      <span className="text-sm text-[#1e3a5f] group-hover:text-[#3b82f6] font-medium">
                        Ətraflı
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product) => (
              <Link key={product.id} to={`/products/${product.id}`} className="block group">
                <Card className="card-hover">
                  <CardContent className="p-4 flex gap-6">
                    <div className="w-32 h-32 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={product.image_url || PRODUCT_PLACEHOLDER}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          {getCategoryName(product.category_id) && (
                            <span className="text-xs text-[#3b82f6] font-medium">
                              {getCategoryName(product.category_id)}
                            </span>
                          )}
                          <h3 className="font-semibold text-lg text-[#1e3a5f] mt-1 group-hover:text-[#3b82f6] transition-colors">
                            {product.name}
                          </h3>
                        </div>
                        {product.show_price && product.price ? (
                          <p className="font-bold text-[#3b82f6] text-xl">{product.price} AZN</p>
                        ) : null}
                      </div>
                      <p className="text-slate-500 mt-2 whitespace-pre-line">
                        {product.description || 'Keyfiyyətli dərman'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
